// packages/shared/src/schemas/notifications.ts

import { z } from 'zod';

// Notification type enum (runtime enum for TypeScript)
export enum NotificationType {
  BOOKING_CONFIRMATION = 'BOOKING_CONFIRMATION',
  BOOKING_REMINDER = 'BOOKING_REMINDER',
  VENDOR_BOOKING_REMINDER = 'VENDOR_BOOKING_REMINDER',
  BOOKING_CANCELLED = 'BOOKING_CANCELLED',
  PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  REFUND_INITIATED = 'REFUND_INITIATED',
  REFUND_COMPLETED = 'REFUND_COMPLETED',
  VENDOR_NEW_BOOKING = 'VENDOR_NEW_BOOKING',
  VENDOR_APPROVAL = 'VENDOR_APPROVAL',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  SYSTEM_ANNOUNCEMENT = 'SYSTEM_ANNOUNCEMENT',
  REVIEW_REQUEST = 'REVIEW_REQUEST',
  PROMOTION = 'PROMOTION',
  SYSTEM = 'SYSTEM',
  PAYOUT_SUCCESS = 'PAYOUT_SUCCESS',
  PAYOUT_FAILURE = 'PAYOUT_FAILURE',
  SUBSCRIPTION_ACTIVATED = 'SUBSCRIPTION_ACTIVATED',
  SUBSCRIPTION_TRIAL_REMINDER = 'SUBSCRIPTION_TRIAL_REMINDER',
  SUBSCRIPTION_DOWNGRADED = 'SUBSCRIPTION_DOWNGRADED',
  SUBSCRIPTION_EXPIRED = 'SUBSCRIPTION_EXPIRED',
  VENDOR_PENDING_CREDIT_ALERT = 'VENDOR_PENDING_CREDIT_ALERT',
  VENDOR_DAILY_CREDIT_SUMMARY = 'VENDOR_DAILY_CREDIT_SUMMARY',
  ADMIN_NEW_BOOKING = 'ADMIN_NEW_BOOKING',
  ADMIN_BOOKING_CANCELLED = 'ADMIN_BOOKING_CANCELLED',
  SESSION_EXPIRING = 'SESSION_EXPIRING',
  BOOKING_CHECKIN = 'BOOKING_CHECKIN',
  BOOKING_PAYMENT_EXPIRED = 'BOOKING_PAYMENT_EXPIRED',
}

// Zod enum for validation (matches the TypeScript enum)
export const NotificationTypeEnum = z.nativeEnum(NotificationType);

// Notification delivery channels
export enum NotificationChannel {
  EMAIL = 'EMAIL',
  PUSH = 'PUSH',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
}

export const NotificationChannelEnum = z.nativeEnum(NotificationChannel);

// Notification delivery status
export enum NotificationStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

export const NotificationStatusEnum = z.nativeEnum(NotificationStatus);

// Channel selection for notifications
export interface ChannelSelection {
  email?: boolean;
  push?: boolean;
  sms?: boolean;
  whatsapp?: boolean;
}

export const ChannelSelectionSchema = z.object({
  email: z.boolean().optional(),
  push: z.boolean().optional(),
  sms: z.boolean().optional(),
  whatsapp: z.boolean().optional(),
});

// Default channels by notification type
export const DEFAULT_CHANNELS_BY_TYPE: Record<NotificationType, ChannelSelection> = {
  [NotificationType.BOOKING_CONFIRMATION]: { email: true, push: true, sms: true, whatsapp: false },
  [NotificationType.BOOKING_REMINDER]: { email: false, push: true, sms: false, whatsapp: false },
  [NotificationType.VENDOR_BOOKING_REMINDER]: { push: true, email: false, sms: false, whatsapp: false },
  [NotificationType.BOOKING_CANCELLED]: { email: true, push: true, sms: true, whatsapp: false },
  [NotificationType.PAYMENT_SUCCESS]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.PAYMENT_FAILED]: { email: true, push: true, sms: true, whatsapp: false },
  [NotificationType.REFUND_INITIATED]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.REFUND_COMPLETED]: { email: true, push: true, sms: true, whatsapp: false },
  [NotificationType.VENDOR_NEW_BOOKING]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.VENDOR_APPROVAL]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.PASSWORD_CHANGED]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.SYSTEM_ANNOUNCEMENT]: { email: false, push: true, sms: false, whatsapp: false },
  [NotificationType.REVIEW_REQUEST]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.PROMOTION]: { email: true, push: true, sms: false, whatsapp: false },
  [NotificationType.SYSTEM]: { email: false, push: true, sms: false, whatsapp: false },
  [NotificationType.PAYOUT_SUCCESS]: { email: true, push: false, sms: false, whatsapp: false },
  [NotificationType.PAYOUT_FAILURE]: { email: true, push: false, sms: false, whatsapp: false },
  [NotificationType.SUBSCRIPTION_ACTIVATED]: {
    email: true,
    push: false,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.SUBSCRIPTION_TRIAL_REMINDER]: {
    email: true,
    push: false,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.SUBSCRIPTION_DOWNGRADED]: {
    email: true,
    push: false,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.SUBSCRIPTION_EXPIRED]: {
    email: true,
    push: false,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.VENDOR_PENDING_CREDIT_ALERT]: {
    email: false,
    push: true,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.VENDOR_DAILY_CREDIT_SUMMARY]: {
    email: true,
    push: true,
    sms: false,
    whatsapp: false,
  },
  [NotificationType.ADMIN_NEW_BOOKING]: { email: true },
  [NotificationType.ADMIN_BOOKING_CANCELLED]: { email: true },
  [NotificationType.SESSION_EXPIRING]: { email: false, push: true, sms: false, whatsapp: false },
  [NotificationType.BOOKING_CHECKIN]: { email: true, push: true, sms: true, whatsapp: false },
  [NotificationType.BOOKING_PAYMENT_EXPIRED]: { email: true, push: true, sms: true, whatsapp: false },
};

// User notification preferences schema
export const UserNotificationPreferenceSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  whatsappEnabled: z.boolean(),
  typePreferences: z.record(z.string(), ChannelSelectionSchema).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type UserNotificationPreference = z.infer<typeof UserNotificationPreferenceSchema>;

// Vendor notification settings schema
export const VendorNotificationSettingsSchema = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  emailEnabled: z.boolean(),
  pushEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  whatsappEnabled: z.boolean(),
  typePreferences: z.record(z.string(), ChannelSelectionSchema).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type VendorNotificationSettings = z.infer<typeof VendorNotificationSettingsSchema>;

// Notification schema
export const NotificationSchema = z.object({
  id: z.string().uuid(),
  type: NotificationTypeEnum,
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(500),
  data: z.record(z.unknown()).optional(),
  actionUrl: z.string().url().optional(),
  referenceId: z.string().uuid().optional(),
  isRead: z.boolean(),
  readAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
});

export type Notification = z.infer<typeof NotificationSchema>;

// API response schemas
export const NotificationListResponseSchema = z.object({
  data: z.array(NotificationSchema),
  total: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  hasMore: z.boolean(),
});

export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().nonnegative(),
});

export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

export const GroupedNotificationsSchema = z.object({
  Today: z.array(NotificationSchema),
  Yesterday: z.array(NotificationSchema),
  'This Week': z.array(NotificationSchema),
  Earlier: z.array(NotificationSchema),
});

export type GroupedNotifications = z.infer<typeof GroupedNotificationsSchema>;

// Device platform enum
export enum DevicePlatform {
  IOS = 'IOS',
  ANDROID = 'ANDROID',
  WEB = 'WEB',
}

export const DevicePlatformEnum = z.nativeEnum(DevicePlatform);

// Device registration schema
export const RegisterDeviceSchema = z.object({
  fcmToken: z.string().min(100, 'FCM token too short - may be a raw APNs token').max(500),
  platform: DevicePlatformEnum,
  deviceName: z.string().optional(),
  deviceModel: z.string().optional(),
});

export type RegisterDeviceDto = z.infer<typeof RegisterDeviceSchema>;

// Device token schema
export const DeviceTokenSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  fcmToken: z.string(),
  platform: DevicePlatformEnum,
  deviceName: z.string().nullable(),
  deviceModel: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DeviceToken = z.infer<typeof DeviceTokenSchema>;

// ============================================================================
// Admin Testing Schemas
// ============================================================================

/**
 * Admin: Send test notification schema
 * Allows admins to test push notifications by sending to any user
 */
export const SendTestNotificationSchema = z.object({
  userId: z.string().uuid(),
  title: z.string().min(1).max(100),
  body: z.string().min(1).max(500),
  type: NotificationTypeEnum.optional(),
  data: z.record(z.string()).optional(),
});

export type SendTestNotificationDto = z.infer<typeof SendTestNotificationSchema>;

/**
 * Admin: Device token response with user info (for debugging)
 */
export const DeviceTokenResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  fcmToken: z.string(),
  platform: DevicePlatformEnum,
  deviceName: z.string().nullable(),
  deviceModel: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  user: z
    .object({
      id: z.string().uuid(),
      email: z.string().email(),
      name: z.string().nullable(),
      role: z.string().optional(),
    })
    .optional(),
});

export type DeviceTokenResponse = z.infer<typeof DeviceTokenResponseSchema>;
